import { Link, useParams } from 'react-router-dom';
import { Decimal, formaterPourcentage } from '@gsm/partage';
import { messageErreur, useRessource } from '../api/ressource';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { JournalEspeces, LIBELLE_MOUVEMENT, montantSigne } from '../composants/JournalEspeces';
import { Nombre } from '../composants/Nombre';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { Note } from '../composants/Note';

interface Engagement {
  nature: 'ORDRE' | 'SORTIE';
  reference: string;
  libelle: string;
  statut: string;
  sens: 'ENTREE' | 'SORTIE';
  montant: string;
}

interface LigneVentilation {
  type: string;
  nb: number;
  entrees: string;
  sorties: string;
  net: string;
}

interface Fiche {
  mandat: {
    id: string;
    reference: string;
    client: { nom: string; prenom: string };
    profil: string;
  };
  solde: string;
  actifNet: string;
  tamponPct: string;
  tamponCible: string;
  ecart: string;
  engagements: Engagement[];
  entreesEngagees: string;
  sortiesEngagees: string;
  soldeProjete: string;
  ventilation: LigneVentilation[];
}

const LIBELLE_NATURE: Record<Engagement['nature'], string> = {
  ORDRE: 'Ordre',
  SORTIE: 'Sortie de fonds',
};

const TON_STATUT: Record<string, TonPastille> = {
  BROUILLON: 'neutre',
  A_VALIDER: 'attente',
  VALIDE: 'interface',
  TRANSMIS: 'interface',
  DENOUE: 'succes',
  OUVERTE: 'attente',
  ORDRES_GENERES: 'interface',
  TRESORERIE_INSUFFISANTE: 'attente',
};

export function FicheTresorerie() {
  const { id } = useParams<{ id: string }>();
  const { donnees, chargement, erreur } = useRessource<Fiche>(`/portefeuilles/${id}/tresorerie`);

  const colonnesEngagements: Colonne<Engagement>[] = [
    { cle: 'nature', entete: 'Nature', rendu: (e) => LIBELLE_NATURE[e.nature] },
    { cle: 'reference', entete: 'Référence', num: true, rendu: (e) => e.reference },
    { cle: 'libelle', entete: 'Objet', rendu: (e) => e.libelle },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (e) => (
        <Pastille ton={TON_STATUT[e.statut] ?? 'neutre'}>
          {e.statut.replace(/_/g, ' ').toLowerCase()}
        </Pastille>
      ),
    },
    {
      cle: 'montant',
      entete: 'Effet sur la trésorerie',
      num: true,
      rendu: (e) => montantSigne(e.sens, e.montant),
    },
  ];

  const colonnesVentilation: Colonne<LigneVentilation>[] = [
    { cle: 'type', entete: 'Type', rendu: (l) => LIBELLE_MOUVEMENT[l.type] ?? l.type },
    { cle: 'nb', entete: 'Écritures', num: true, rendu: (l) => l.nb },
    { cle: 'entrees', entete: 'Entrées', num: true, rendu: (l) => <Nombre valeur={l.entrees} /> },
    { cle: 'sorties', entete: 'Sorties', num: true, rendu: (l) => <Nombre valeur={l.sorties} /> },
    { cle: 'net', entete: 'Net', num: true, rendu: (l) => <Nombre valeur={l.net} /> },
  ];

  if (chargement) return <p className="surtitre">Chargement…</p>;
  if (erreur || !donnees)
    return (
      <BandeauAlerte nature="refus" titre="Mandat introuvable">
        {messageErreur(erreur) ?? 'Ce mandat n’existe pas.'}
      </BandeauAlerte>
    );

  const f = donnees;
  const sousTampon = new Decimal(f.ecart).lt(0);
  const projeteSousTampon = new Decimal(f.soldeProjete).lt(f.tamponCible);

  return (
    <>
      <p className="entete__fil">
        <Link to="/tresorerie">Trésorerie</Link> ›{' '}
        <Link to={`/mandats/${f.mandat.id}`}>{f.mandat.reference}</Link> › Espèces
      </p>
      <div className="entete">
        <h1>
          {f.mandat.client.prenom} {f.mandat.client.nom}
        </h1>
        <span className="entete__ref">{f.mandat.reference}</span>
        <Pastille ton="neutre">{f.mandat.profil}</Pastille>
      </div>

      <section className="cles" aria-label="Chiffres clés">
        <div className="cle">
          <span className="surtitre">Solde</span>
          <div className="cle__valeur">
            <Nombre valeur={f.solde} />
            <small>MAD</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Tampon cible</span>
          <div className="cle__valeur">
            <Nombre valeur={f.tamponCible} />
            <small>MAD</small>
          </div>
          <div className="appoint">{formaterPourcentage(f.tamponPct)} de l’actif net</div>
        </div>
        <div className="cle">
          <span className="surtitre">Écart au tampon</span>
          <div className="cle__valeur">
            <Nombre valeur={f.ecart} />
            <small>MAD</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Engagé</span>
          <div className="cle__valeur">
            <Nombre valeur={f.sortiesEngagees} />
            <small>MAD</small>
          </div>
          <div className="appoint">
            {new Decimal(f.entreesEngagees).gt(0) ? (
              <>
                dont <Nombre valeur={f.entreesEngagees} /> à recevoir
              </>
            ) : (
              'aucune entrée attendue'
            )}
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Solde projeté</span>
          <div className="cle__valeur">
            <Nombre valeur={f.soldeProjete} />
            <small>MAD</small>
          </div>
          <div className="appoint">une fois les engagements dénoués</div>
        </div>
      </section>

      {sousTampon && (
        <BandeauAlerte nature="gestion" titre="Trésorerie sous le tampon">
          Le solde est inférieur à la cible de {formaterPourcentage(f.tamponPct)}. Le mandat n’a
          plus de réserve pour absorber un décaissement, et le moteur n’a rien à investir.
        </BandeauAlerte>
      )}

      {!sousTampon && projeteSousTampon && (
        <BandeauAlerte nature="gestion" titre="Le projeté passe sous le tampon">
          Le solde d’aujourd’hui couvre la cible, mais pas une fois les engagements dénoués. C’est
          l’écart que le solde seul ne montre pas.
        </BandeauAlerte>
      )}

      <Bloc titre="Engagements" meta={`${f.engagements.length} en cours`}>
        <Tableau
          colonnes={colonnesEngagements}
          lignes={f.engagements}
          cle={(e, i) => `${e.nature}-${e.reference}-${i}`}
          vide="Rien d’engagé : le solde projeté est le solde."
          pied={
            f.engagements.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <b>Solde projeté</b>
                  </td>
                  <td className="num">
                    <Nombre valeur={f.soldeProjete} />
                  </td>
                </tr>
              </tfoot>
            ) : undefined
          }
        />
        <Note resume="Un achat non dénoué sortira, une vente rentrera, une sortie de fonds ouverte sortira à la mise à disposition des fonds.">
          Pour un ordre partiellement exécuté, seul le reste compte : la part servie a déjà bougé le
          solde.
        </Note>
      </Bloc>

      <JournalEspeces mandatId={f.mandat.id} />

      <Bloc titre="Depuis l’origine" meta="tous mouvements confondus">
        <Tableau
          colonnes={colonnesVentilation}
          lignes={f.ventilation}
          cle={(l) => l.type}
          vide="Aucun mouvement."
        />
      </Bloc>
    </>
  );
}
